/**
 * Code Browser Component (Left Panel)
 *
 * Displays hierarchical list of behavioral codes organized by dimension.
 * Features:
 * - Collapsible categories (6 Faana dimensions)
 * - Code search/filter
 * - Valence indicators (positive/negative/neutral)
 * - Keyboard shortcuts
 * - Usage counts per code
 */

import React from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Collapse,
  List,
  ListItemButton,
  ListItemText,
  Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import {
  FAANA_DIMENSIONS,
  DIMENSION_COLORS,
  VALENCE_COLORS,
} from '../../constants/workbenchConfig';

/**
 * Code Browser Panel
 */
// eslint-disable-next-line max-lines-per-function
function CodeBrowser({
  _codes,
  codesByDimension,
  expandedCategories,
  onToggleCategory,
  searchTerm,
  onSearchChange,
  searchInputRef,
  onCodeClick,
  onNewCode,
  onEditCode,
  learningMode,
  selectedChunk,
  isAdmin,
}) {
  // Count usage of each code in current chunk
  const usedCodes = selectedChunk?.behavioral_codes?.map((c) => c.code) || [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Search Header */}
      <Box sx={{ p: 2, pb: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search codes..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          inputRef={searchInputRef}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => onSearchChange('')}
                  edge="end"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: 'rgba(0,0,0,0.02)',
            },
          }}
        />
      </Box>

      {/* Category List */}
      <Box sx={{ flex: 1, overflow: 'auto', pb: 2 }}>
        {FAANA_DIMENSIONS.map((dimension) => {
          const dimensionCodes = codesByDimension[dimension] || [];
          const isExpanded = expandedCategories[dimension];
          const hasCodeInUse = dimensionCodes.some((c) =>
            usedCodes.includes(c.code)
          );

          return (
            <Box key={dimension}>
              {/* Category Header */}
              <ListItemButton
                onClick={() => onToggleCategory(dimension)}
                sx={{
                  py: 1,
                  px: 2,
                  bgcolor: hasCodeInUse
                    ? 'rgba(35, 163, 143, 0.08)'
                    : 'transparent',
                  '&:hover': {
                    bgcolor: 'rgba(0,0,0,0.04)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: DIMENSION_COLORS[dimension],
                    mr: 1.5,
                    flexShrink: 0,
                  }}
                />
                <ListItemText
                  primaryTypographyProps={{ component: 'div' }}
                  primary={
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, color: 'text.primary' }}
                    >
                      {dimension}
                    </Typography>
                  }
                />
                <Chip
                  label={dimensionCodes.length}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    mr: 1,
                    bgcolor: 'rgba(0,0,0,0.08)',
                  }}
                />
                {isExpanded ? (
                  <ExpandLessIcon fontSize="small" color="action" />
                ) : (
                  <ExpandMoreIcon fontSize="small" color="action" />
                )}
              </ListItemButton>

              {/* Codes in Category */}
              <Collapse in={isExpanded}>
                <List disablePadding>
                  {dimensionCodes.map((code) => {
                    const isUsed = usedCodes.includes(code.code);

                    return (
                      <CodeItem
                        key={code.id || code.code}
                        code={code}
                        isUsed={isUsed}
                        onClick={() => onCodeClick(code)}
                        onEdit={isAdmin ? () => onEditCode?.(code) : undefined}
                        learningMode={learningMode}
                      />
                    );
                  })}
                </List>
              </Collapse>
            </Box>
          );
        })}
      </Box>

      {/* Footer - New Code Button (admin only) */}
      {isAdmin && (
        <Box
          sx={{
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Tooltip title="Create new behavioral code (Shift+N)">
            <Box
              onClick={onNewCode}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: 'text.secondary',
                cursor: 'pointer',
                opacity: 0.7,
                '&:hover': { opacity: 1, color: 'primary.main' },
              }}
            >
              <AddIcon fontSize="small" />
              <Typography variant="body2">New Code</Typography>
            </Box>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
}

/**
 * Individual Code Item
 */
function CodeItem({ code, isUsed, onClick, onEdit, learningMode }) {
  return (
    <Tooltip
      title={learningMode ? code.description : ''}
      placement="right"
      arrow
    >
      <ListItemButton
        onClick={onClick}
        sx={{
          py: 0.75,
          pl: 4,
          pr: 2,
          bgcolor: isUsed ? 'rgba(35, 163, 143, 0.12)' : 'transparent',
          borderLeft: isUsed ? '3px solid' : '3px solid transparent',
          borderLeftColor: isUsed ? 'accent.teal' : 'transparent',
          '&:hover': {
            bgcolor: isUsed
              ? 'rgba(35, 163, 143, 0.18)'
              : 'rgba(0,0,0,0.04)',
          },
          '& .edit-icon': { opacity: 0 },
          '&:hover .edit-icon': { opacity: 1 },
        }}
      >
        {/* Valence Indicator */}
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: VALENCE_COLORS[code.valence] || VALENCE_COLORS.neutral,
            mr: 1.5,
            flexShrink: 0,
          }}
        />

        {/* Code Name */}
        <ListItemText
          primaryTypographyProps={{ component: 'div' }}
          primary={
            <Typography
              variant="body2"
              sx={{
                fontWeight: isUsed ? 600 : 400,
                color: 'text.primary',
                fontSize: '0.85rem',
              }}
            >
              {code.displayName}
            </Typography>
          }
        />

        {/* Edit Button (admin only) */}
        {onEdit && (
          <IconButton
            className="edit-icon"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            sx={{ p: 0.25, mr: 0.5, transition: 'opacity 0.2s' }}
          >
            <EditIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}

        {/* Keyboard Shortcut Badge */}
        {code.shortcut && (
          <Chip
            label={code.shortcut}
            size="small"
            sx={{
              height: 18,
              fontSize: '0.65rem',
              fontWeight: 600,
              bgcolor: 'rgba(0,0,0,0.08)',
              color: 'text.secondary',
              minWidth: 24,
            }}
          />
        )}
      </ListItemButton>
    </Tooltip>
  );
}

export default CodeBrowser;
